import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { noteTypeToDivisionsPerMeasure } from 'Utils';
import TimeSignature from './TimeSignature';
import * as Tone from 'tone';
import ITimeKeeper from './ITimeKeeper';

// Almost exactly like the regular time keeper, except it's not backed by an audio context,
// so you can set its time.
export default class MockTimeKeeper implements ITimeKeeper {
  public static get METRONOME_SCHEDULE_AHEAD_TIME(): number {
    return 0.2;
  }

  // Configuration parameters (these are set by the outside world)
  // Beats per minute
  private bpm: number = -1;
  // Eventually we'll handle time signatures per measure; this is prepping for that.
  // For now we just look at the first time signature in this array.
  private timeSignatures: TimeSignature[] = [];
  // TODO make me configurable...
  // private timeSignature: Fraction = new Fraction(4, 4, 1, false)
  // When unpaused, the TimeKeeper will count in to this measure.
  private startMeasure: number = 0;
  // The number of measures we should spend counting in.
  private countInMeasures: number = 1;
  // Determines how many ticks there are per beat.
  private tickNoteType: NoteType = NoteType._32nd;

  // State tracking parameters, some of these are exposed to the outside world.
  private paused: boolean = true;
  // private elapsedTicks: number = 0
  private elapsedTicksOffset: number = 0;
  // Audio time in seconds when playback should begin.
  private startTime: number = 0;
  private measureDurationSecs: number = 0;
  private ticksPerMeasure: number = 0;
  private secondsPerTick: number = 0;

  private currentTime: number = 0;

  unpause(bpm: number, countInMeasures: number, startMeasure: number, tickNoteType: NoteType) {
    this.bpm = bpm;
    this.countInMeasures = countInMeasures;
    this.startMeasure = startMeasure;
    this.tickNoteType = tickNoteType;
    // For now we assume time signatures won't change per-lesson...
    const timeSignature = this.timeSignatures[0];
    if (!timeSignature) {
      return;
    }

    if (timeSignature.denominator === 4) {
      this.measureDurationSecs = (60.0 / this.bpm) * timeSignature.numerator;
    } else {
      throw `Time signature with denominator of ${timeSignature.denominator} is not implemented (yet)!`;
    }

    // Initialize some important timing numbers.

    this.ticksPerMeasure = noteTypeToDivisionsPerMeasure(this.tickNoteType, timeSignature);
    this.secondsPerTick = this.measureDurationSecs / this.ticksPerMeasure;
    this.elapsedTicksOffset = this.ticksPerMeasure * this.startMeasure;

    // Calculate the first beat of user MIDI playback.
    this.startTime = 0;

    // Debug logging...
    // console.log(`bpm: ${bpm}, measure duration: ${this.measureDurationSecs}, tick duration: ${this.secondsPerTick}`)

    this.paused = false;
  }

  pause() {
    this.paused = true;
  }

  resetTime() {}

  isPaused() {
    return this.paused;
  }

  setBpm(newBpm: number) {
    // If we aren't currently playing back, we'll update BPM numbers later
    const oldBpm = this.bpm;
    this.bpm = newBpm;

    if (!this.paused) {
      // BPM adjustment during playback requires us to move the start time.
      // We're going to move the start time so it scales to the new BPM.
      const timeSinceStart = this.currentTime - this.startTime;
      const bpmScale = oldBpm / newBpm;
      this.startTime = this.currentTime - timeSinceStart * bpmScale;

      // Recalculate affected numbers.
      this.measureDurationSecs = (60.0 / this.bpm) * this.timeSignatures[0].numerator;
      this.secondsPerTick = this.measureDurationSecs / this.ticksPerMeasure;
    }
  }

  setTimeSignatures(startMeasure: number, timeSignatures: TimeSignature[]) {
    // First overwrite any time signatures that we already store,
    // and then push the new ones.
    let i = 0;
    for (
      let j = startMeasure;
      i < timeSignatures.length && j < this.timeSignatures.length;
      i++, j++
    ) {
      this.timeSignatures[j] = timeSignatures[i];
    }
    for (; i < timeSignatures.length; i++) {
      this.timeSignatures.push(timeSignatures[i]);
    }
  }

  calculateElapsedTicks(): number {
    const elapsedTime = this.currentTime - this.startTime;
    return Math.floor(elapsedTime / this.secondsPerTick) + this.elapsedTicksOffset;
  }

  noteTypeToTicksPerMeasure(noteType: NoteType) {
    const timeSignature = this.timeSignatures[0];
    return noteTypeToDivisionsPerMeasure(noteType, timeSignature);
  }

  // Convert an OSMD timestamp to ticks.
  // Note this will return non-integer ticks if the math works out that way!
  measureTimestampToTicks(measureTimestamp: number): number {
    return this.ticksPerMeasure * measureTimestamp;
  }

  measureDurationAt(_index: number) {
    const timeSignature = this.timeSignatures[0];
    return timeSignature?.numerator / timeSignature?.denominator;
  }

  // BPM-aware conversion of a measure timestamp to the current audio time.
  measureTimestampToAudioTime(measureTimestamp: number): number {
    const measureOffset = measureTimestamp - this.startMeasure;
    return this.startTime + measureOffset * this.measureDurationSecs;
  }

  audioTimeToMeasureTimestamp(): number {
    const timeOffset = this.currentTime - this.startTime;
    const timestamp = this.startMeasure + timeOffset / this.measureDurationSecs;
    return timestamp * this.measureDurationAt(0);
  }

  setCurrentTime(time: number) {
    this.currentTime = time;
  }

  // TODO a lot of these could maybe be setters idk

  getPlaybackStartTicks(): number {
    return this.elapsedTicksOffset;
  }

  getPlaybackStartTime(): number {
    return this.startTime;
  }

  getTicksPerMeasure(): number {
    return this.ticksPerMeasure;
  }

  getAudioContext(): Tone.BaseContext {
    return Tone.context;
  }

  getAudioContextTime(): number {
    return this.currentTime;
  }

  getTimeSignature(): TimeSignature {
    return this.timeSignatures[0];
  }

  getTimeSigFract(): number {
    return this.timeSignatures[0].numerator / this.timeSignatures[0].denominator;
  }

  getTickNoteType(): NoteType {
    return this.tickNoteType;
  }

  getMeasureDurationSecs(): number {
    return this.measureDurationSecs;
  }

  getBpm(): number {
    return this.bpm;
  }

  getIsCountingIn(): boolean {
    return this.currentTime < this.startTime;
  }
}
